'use client';

import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Webhook, Plus, Trash2, ChevronDown, ChevronRight, Save, Loader2, X, Copy, CheckCircle2, Send, AlertTriangle,  } from 'lucide-react';
import { toast } from 'sonner';
import { configStore } from '@/lib/configStore';
import type { WebhookConfig } from '@/lib/types';

interface WebhookFormData {
  name: string;
  url: string;
  method: 'POST' | 'PUT';
  authHeader: string;
}

export default function WebhookPanel() {
  const [webhooks, setWebhooks] = useState<WebhookConfig[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [expandedPayload, setExpandedPayload] = useState<string | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  useEffect(() => {
    setWebhooks(configStore.getWebhooks());
  }, []);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<WebhookFormData>({
    defaultValues: { name: '', url: '', method: 'POST', authHeader: '' },
  });

  const samplePayload = {
    event: 'media_dispatched',
    timestamp: '2026-07-08T05:55:02Z',
    source: 'MediaDispatch',
    item: {
      id: 'series-001',
      title: 'Example Series',
      type: 'series',
      year: 2023,
      status: 'Continuing',
      genres: ['Drama', 'Thriller'],
      rating: 8.4,
      seasonCount: 3,
      qualityProfile: 'HD-1080p',
      monitored: true,
    },
  };

  const onAddWebhook = async (data: WebhookFormData) => {
    const newWh: WebhookConfig = {
      id: `webhook-${Date.now()}`,
      name: data.name,
      url: data.url,
      method: data.method,
      authHeader: data.authHeader || undefined,
      createdAt: new Date().toISOString(),
    };
    const updated = [...webhooks, newWh];
    setWebhooks(updated);
    configStore.saveWebhooks(updated);
    setShowAddForm(false);
    reset();
    toast.success(`Webhook "${data.name}" added`);
  };

  const deleteWebhook = (id: string) => {
    const wh = webhooks.find((w) => w.id === id);
    const updated = webhooks.filter((w) => w.id !== id);
    setWebhooks(updated);
    configStore.saveWebhooks(updated);
    setDeleteConfirmId(null);
    toast.success(`Webhook "${wh?.name}" removed`);
  };

  const testWebhook = async (wh: WebhookConfig) => {
    setTestingId(wh.id);
    // BACKEND INTEGRATION: Send test POST to wh.url with samplePayload
    await new Promise((r) => setTimeout(r, 1500));
    setTestingId(null);
    const ok = wh.url.startsWith('http');
    if (ok) {
      toast.success(`Test sent to "${wh.name}" — 200 OK`);
    } else {
      toast.error(`Test failed for "${wh.name}" — connection refused`);
    }
  };

  const copyPayload = () => {
    navigator.clipboard.writeText(JSON.stringify(samplePayload, null, 2));
    toast.success('Payload schema copied to clipboard');
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Panel header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-muted border border-border">
            <Webhook size={18} className="text-muted-foreground" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-foreground">Webhooks</h2>
            <p className="text-xs text-muted-foreground">
              {webhooks.length} endpoint{webhooks.length !== 1 ? 's' : ''} configured
            </p>
          </div>
        </div>
        <button
          onClick={() => { setShowAddForm(true); setEditingId(null); }}
          className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium btn-primary"
        >
          <Plus size={14} />
          Add Webhook
        </button>
      </div>

      {/* Add form */}
      {showAddForm && (
        <div className="card-elevated p-5 fade-in">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-foreground">New Webhook</h3>
            <button onClick={() => { setShowAddForm(false); reset(); }} className="btn-ghost p-1 rounded-lg">
              <X size={14} />
            </button>
          </div>
          <form onSubmit={handleSubmit(onAddWebhook)} className="flex flex-col gap-4">
            {/* Name */}
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">
                Webhook Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                placeholder="e.g. Jellyfin Notifier"
                className={`input-base w-full px-3 py-2 text-sm ${errors.name ? 'border-danger' : ''}`}
                {...register('name', { required: 'Name is required' })}
              />
              {errors.name && <p className="text-2xs text-danger mt-1">{errors.name.message}</p>}
            </div>

            {/* URL */}
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">
                Endpoint URL <span className="text-danger">*</span>
              </label>
              <p className="text-2xs text-muted-foreground mb-1.5">
                Each selected item sends one POST to this URL
              </p>
              <input
                type="url"
                placeholder="https://your-service.com/webhook"
                className={`input-base w-full px-3 py-2 text-sm font-mono-data ${errors.url ? 'border-danger' : ''}`}
                {...register('url', {
                  required: 'URL is required',
                  pattern: { value: /^https?:\/\/.+/, message: 'Must be a valid http/https URL' },
                })}
              />
              {errors.url && <p className="text-2xs text-danger mt-1">{errors.url.message}</p>}
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Method */}
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">
                  HTTP Method
                </label>
                <select
                  className="input-base w-full px-3 py-2 text-sm"
                  {...register('method')}
                >
                  <option value="POST">POST</option>
                  <option value="PUT">PUT</option>
                </select>
              </div>

              {/* Auth header */}
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">
                  Authorization Header
                </label>
                <input
                  type="text"
                  placeholder="Bearer token (optional)"
                  className="input-base w-full px-3 py-2 text-sm font-mono-data"
                  {...register('authHeader')}
                />
              </div>
            </div>

            <div className="flex items-center gap-2 pt-1">
              <button
                type="button"
                onClick={() => { setShowAddForm(false); reset(); }}
                className="px-4 py-2 text-sm font-medium btn-ghost"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex items-center gap-2 px-5 py-2 text-sm font-semibold btn-primary"
              >
                <Save size={14} />
                Save Webhook
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Webhook list */}
      {webhooks.length === 0 ? (
        <div className="card-elevated flex flex-col items-center justify-center py-14 text-center">
          <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center mb-3">
            <Webhook size={22} className="text-muted-foreground" />
          </div>
          <h3 className="text-sm font-semibold text-foreground mb-1">No webhooks yet</h3>
          <p className="text-xs text-muted-foreground max-w-xs">
            Add a webhook endpoint to start dispatching media payloads from the library.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {webhooks.map((wh) => (
            <div key={wh.id} className="card-elevated overflow-hidden">
              <div className="flex items-center gap-3 p-4">
                <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                  <Webhook size={15} className="text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground">{wh.name}</p>
                  <p className="text-2xs text-muted-foreground font-mono-data truncate mt-0.5">
                    {wh.method} · {wh.url}
                  </p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {/* Test */}
                  <button
                    onClick={() => testWebhook(wh)}
                    disabled={testingId === wh.id}
                    title={`Send test payload to ${wh.name}`}
                    className="btn-ghost p-2 rounded-lg disabled:opacity-50"
                  >
                    {testingId === wh.id ? (
                      <Loader2 size={14} className="animate-spin text-primary" />
                    ) : (
                      <Send size={14} />
                    )}
                  </button>

                  {/* Payload preview toggle */}
                  <button
                    onClick={() => setExpandedPayload(expandedPayload === wh.id ? null : wh.id)}
                    title="Preview payload schema"
                    className="btn-ghost p-2 rounded-lg"
                  >
                    {expandedPayload === wh.id ? (
                      <ChevronDown size={14} />
                    ) : (
                      <ChevronRight size={14} />
                    )}
                  </button>

                  {/* Delete */}
                  {deleteConfirmId === wh.id ? (
                    <div className="flex items-center gap-1 ml-1">
                      <button
                        onClick={() => deleteWebhook(wh.id)}
                        className="px-2 py-1 text-2xs font-semibold rounded-md bg-danger text-white"
                      >
                        Delete
                      </button>
                      <button
                        onClick={() => setDeleteConfirmId(null)}
                        className="btn-ghost px-2 py-1 text-2xs"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setDeleteConfirmId(wh.id)}
                      title={`Remove ${wh.name} — this cannot be undone`}
                      className="btn-ghost p-2 rounded-lg text-muted-foreground hover:text-danger"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>

              {/* Payload preview */}
              {expandedPayload === wh.id && (
                <div className="border-t border-border px-4 pb-4 pt-3">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-2xs font-semibold text-muted-foreground uppercase tracking-wider">
                      JSON Payload Schema
                    </p>
                    <button
                      onClick={copyPayload}
                      className="flex items-center gap-1 text-2xs btn-ghost px-2 py-1 rounded"
                    >
                      <Copy size={11} />
                      Copy
                    </button>
                  </div>
                  <pre className="text-2xs font-mono-data text-muted-foreground bg-input rounded-lg p-3 overflow-x-auto scrollbar-thin leading-relaxed">
                    {JSON.stringify(samplePayload, null, 2)}
                  </pre>
                  <div className="flex items-start gap-2 mt-3 p-2.5 rounded-lg connection-idle">
                    <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />
                    <p className="text-2xs">
                      One payload is sent per selected item. The <code className="font-mono-data">item</code> field is populated from Sonarr or Radarr data for that specific title.
                    </p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Payload schema reference */}
      <div className="card-elevated p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <CheckCircle2 size={14} className="text-success" />
            <p className="text-xs font-semibold text-foreground">Payload Reference</p>
          </div>
          <button
            onClick={copyPayload}
            className="flex items-center gap-1 text-2xs btn-ghost px-2 py-1 rounded"
          >
            <Copy size={11} />
            Copy schema
          </button>
        </div>
        <p className="text-2xs text-muted-foreground mb-3">
          Every dispatch sends this JSON structure. Fields vary slightly between series (Sonarr) and movies (Radarr).
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {[
            { key: 'payload-field-event', field: 'event', desc: 'Always "media_dispatched"' },
            { key: 'payload-field-source', field: 'source', desc: 'Always "MediaDispatch"' },
            { key: 'payload-field-timestamp', field: 'timestamp', desc: 'ISO 8601 dispatch time' },
            { key: 'payload-field-item-id', field: 'item.id', desc: 'Sonarr/Radarr item ID' },
            { key: 'payload-field-item-title', field: 'item.title', desc: 'Full media title' },
            { key: 'payload-field-item-type', field: 'item.type', desc: '"series" or "movie"' },
            { key: 'payload-field-item-status', field: 'item.status', desc: 'Current item status' },
            { key: 'payload-field-item-quality', field: 'item.qualityProfile', desc: 'From settings' },
          ].map((row) => (
            <div key={row.key} className="flex items-start gap-2 p-2 rounded-md bg-input">
              <code className="text-2xs font-mono-data text-primary flex-shrink-0">{row.field}</code>
              <span className="text-2xs text-muted-foreground">{row.desc}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}